import {ConfirmError, loadStripe} from '@stripe/stripe-js';
import React, {useCallback, useState} from 'react';
import {CheckoutProvider, PaymentElement, useCheckout} from '@stripe/react-stripe-js';
import {useAuthState} from "react-firebase-hooks/auth";
import {getAuth} from "firebase/auth";
import {isNullOrUndefined} from "helpers/is-null-or-undefined";
import {useStripeCustomerId} from "hooks/stripe/useStripeCustomerId";
import {httpsCallable} from "firebase/functions";
import {functions} from "hooks/firebase";
import {
  StripeCreateCheckoutSessionRequest, StripeCreateCheckoutSessionResponse
} from "data/models/shared/stripe-create-checkout-session";
import {Button} from "flowbite-react";
import Loading from "components/Loading";

const stripePromise = loadStripe(import.meta.env.VITE_APP_STRIPE_PUBLISHABLE_KEY.trim() as string);

const CheckoutFormInternal = () => {
  const auth = getAuth();
  const [user] = useAuthState(auth);
  const checkout = useCheckout();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ConfirmError | null>(null);

  const handleSubmitPayment = async () => {
    if (isNullOrUndefined(user)) {
      return;
    }
    setLoading(true);
    const result = await checkout.confirm()
    if (result.type === 'error') {
      console.error(result.error)
      setError(result.error)
      return;
    }
    setLoading(false);
  };

  if (isNullOrUndefined(user)) {
    return null;
  }

  return (<>
    <form className={"mt-8"}>
      <PaymentElement options={{layout: 'accordion'}}/>
    </form>
    <div className={"grow flex flex-row mt-8 justify-center"}>
      <Button
          className={"ml-1 px-8 rounded-full"}
          color={"blue"}
          isProcessing={loading}
          disabled={!checkout.canConfirm}
          onClick={handleSubmitPayment}
      >
        <div className={"flex flex-col"}>
        <div className={"text-xl font-bold"}>Buy Now</div>
        <div className={"text-sm"}>$15/year</div>
        </div>
      </Button>
      {error && <div>{error.message}</div>}
    </div>
    <div className={"mt-8 text-center text-md text-neutral-500"}>
      Your updated Brand Style Guide, formatted to use with Canva or other creative tools, will be generated as soon as the payment is processed.
    </div>
  </>);
}


export const CheckoutForm = () => {
  const auth = getAuth();
  const [user] = useAuthState(auth);
  const [customerId, setCustomerId, customerIdLoading, customerIdError] = useStripeCustomerId();
  const [loading, setLoading] = useState(true);

  const fetchClientSecret = useCallback(async (): Promise<string> => {
    const email = user?.email;
    const name = user?.displayName;
    if (isNullOrUndefined(email) || isNullOrUndefined(name)) {
      throw new Error('User is not logged in');
    }
    const stripeCreateCheckoutSessionFunction = httpsCallable<StripeCreateCheckoutSessionRequest, StripeCreateCheckoutSessionResponse>(functions, "stripeCreateCheckoutSession");

    const result = await stripeCreateCheckoutSessionFunction({
      customerId: customerId ?? null,
      name,
      email,
      productsToPurchase: [{
        id: 'bc-express-annual',
      }]
    })
    setLoading(false);
    if (!("customerId" in result.data)) {
      throw new Error('Invalid response from server');
    }
    await setCustomerId(result.data.customerId)
    return result.data.checkoutSessionClientSecret;
  }, [user?.email, customerId]);

  if (isNullOrUndefined(user)) {
    return null
  }

  return <>
    {loading && <Loading className={"mt-8"} loadingText={"Initializing Payment Form..."}/>}
    {!customerIdLoading && customerIdError === null && <CheckoutProvider stripe={stripePromise} options={{fetchClientSecret}}>
      <CheckoutFormInternal/>
    </CheckoutProvider>}
  </>
}
